import React, { useCallback, useEffect, useState } from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import type { Engine, ISourceOptions } from '@tsparticles/engine';

interface ParticlesBackgroundProps {
  className?: string;
  variant?: 'default' | 'minimal' | 'dense';
}

const particleConfigs: Record<string, ISourceOptions> = {
  default: {
    fullScreen: false,
    background: {
      color: { value: 'transparent' },
    },
    fpsLimit: 60,
    particles: {
      number: {
        value: 50,
        density: { enable: true, width: 800, height: 800 },
      },
      color: { value: '#0A84FF' },
      shape: { type: 'circle' },
      opacity: {
        value: 0.5,
        animation: { enable: true, speed: 1, minimumValue: 0.1 },
      },
      size: {
        value: { min: 1, max: 3 },
        animation: { enable: true, speed: 2, minimumValue: 0.5 },
      },
      links: {
        enable: true,
        distance: 150,
        color: '#5856D6',
        opacity: 0.3,
        width: 1,
      },
      move: {
        enable: true,
        speed: 1,
        direction: 'none',
        outModes: { default: 'bounce' },
      },
    },
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: 'grab',
        },
        onClick: {
          enable: true,
          mode: 'push',
        },
      },
      modes: {
        grab: {
          distance: 140,
          links: { opacity: 0.5 },
        },
        push: { quantity: 4 },
      },
    },
    detectRetina: true,
  },
  minimal: {
    fullScreen: false,
    background: { color: { value: 'transparent' } },
    fpsLimit: 30,
    particles: {
      number: { value: 25 },
      color: { value: '#0A84FF' },
      opacity: { value: 0.3 },
      size: { value: { min: 1, max: 2 } },
      links: {
        enable: true,
        distance: 200,
        color: '#5856D6',
        opacity: 0.2,
        width: 1,
      },
      move: { enable: true, speed: 0.5 },
    },
    detectRetina: true,
  },
  dense: {
    fullScreen: false,
    background: { color: { value: 'transparent' } },
    fpsLimit: 60,
    particles: {
      number: { value: 100 },
      color: { value: ['#0A84FF', '#5856D6', '#00e676'] },
      opacity: { value: 0.6 },
      size: { value: { min: 1, max: 4 } },
      links: {
        enable: true,
        distance: 120,
        color: '#5856D6',
        opacity: 0.4,
        width: 1,
      },
      move: { enable: true, speed: 1.5 },
    },
    interactivity: {
      events: {
        onHover: { enable: true, mode: 'repulse' },
      },
      modes: {
        repulse: { distance: 100, duration: 0.4 },
      },
    },
    detectRetina: true,
  },
};

export const ParticlesBackground: React.FC<ParticlesBackgroundProps> = ({
  className = '',
  variant = 'default',
}) => {
  const [init, setInit] = useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine: Engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  const particlesLoaded = useCallback(async (container: any) => {
    // Particles loaded callback if needed
  }, []);

  if (!init) {
    return null;
  }

  return (
    <Particles
      id="tsparticles"
      className={`absolute inset-0 pointer-events-none ${className}`}
      particlesLoaded={particlesLoaded}
      options={particleConfigs[variant]}
    />
  );
};

export default ParticlesBackground;
